import { z } from 'zod';

const point = z.object({ x: z.number().int(), y: z.number().int() });
const color = z.number().int().min(0).max(0xffffff);
const weapon = z.object({
  damage: z.number().int().positive(),
  range: z.number().int().positive(),
  sight: z.number().int().positive(),
  sightClip: z.number().int().nonnegative().optional(),
  sightClipSide: z.number().int().nonnegative().optional(),
  cooldownTicks: z.number().int().positive(),
  windupTicks: z.number().int().nonnegative(),
  projectileSpeed: z.number().int().positive().optional(),
  splashRadius: z.number().int().nonnegative().optional(),
  towerDamageMultiplier: z.number().positive().max(1).optional(),
}).superRefine((value, context) => {
  if ((value.sightClip ?? 0) >= value.sight) context.addIssue({ code: 'custom', message: 'sightClip must be smaller than sight' });
  if ((value.sightClipSide ?? 0) >= value.sight) context.addIssue({ code: 'custom', message: 'sightClipSide must be smaller than sight' });
});
const spawn = z.object({ unitId: z.string().min(1), count: z.number().int().positive(), formationRadius: z.number().int().nonnegative(), staggerTicks: z.number().int().nonnegative().optional() });
const areaDamage = z.object({ damage: z.number().int().positive(), radius: z.number().int().positive(), towerDamageMultiplier: z.number().positive().max(1), visualKey: z.string().min(1) });

export const unitDefinitionSchema = z.object({
  id: z.string().min(1),
  movement: z.enum(['ground', 'air', 'static']),
  hp: z.number().int().positive(),
  radius: z.number().int().positive(),
  mass: z.number().int().positive(),
  speedPerTick: z.number().int().nonnegative(),
  targetMask: z.enum(['ground', 'air-ground', 'buildings']),
  weapon: weapon.optional(),
  lifetimeTicks: z.number().int().positive().optional(),
  deployEffect: areaDamage.optional(),
  deathEffect: areaDamage.extend({ delayTicks: z.number().int().nonnegative() }).optional(),
  periodicSpawn: z.object({ groups: z.array(spawn).min(1), initialDelayTicks: z.number().int().nonnegative(), intervalTicks: z.number().int().positive() }).optional(),
  leapAttack: areaDamage.extend({ minRange: z.number().int().positive(), maxRange: z.number().int().positive(), durationTicks: z.number().int().positive(), cooldownTicks: z.number().int().nonnegative() }).refine((value) => value.maxRange > value.minRange, 'maxRange must exceed minRange').optional(),
  visualKey: z.string().min(1),
});

export const cardDefinitionSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    nameKey: z.string().min(1),
    kind: z.enum(['troop', 'building', 'spell']),
    cost: z.number().int().min(1).max(10),
    deployTimeTicks: z.number().int().nonnegative(),
    placement: z.enum(['own-side', 'anywhere']),
    roles: z.array(z.enum(['tank', 'melee', 'ranged', 'splash', 'swarm', 'anti-air', 'defense', 'spell'])),
    mechanics: z.array(z.discriminatedUnion('type', [
      z.object({
        type: z.literal('spawn-units'),
        groups: z.array(spawn).min(1),
      }),
      z.object({ type: z.literal('area-projectile'), spellId: z.string().min(1) }),
    ])).min(1),
    presentationKey: z.string().min(1),
  })
  .superRefine((card, context) => {
    if (card.kind === 'spell' && !card.mechanics.some((mechanic) => mechanic.type === 'area-projectile')) context.addIssue({ code: 'custom', message: 'Spell card requires an area-projectile mechanic' });
    if (card.kind !== 'spell' && !card.mechanics.some((mechanic) => mechanic.type === 'spawn-units')) context.addIssue({ code: 'custom', message: 'Unit/building card requires a spawn-units mechanic' });
  });

export const spellDefinitionSchema = z
  .object({
    id: z.string().min(1),
    damage: z.number().int().nonnegative(),
    radius: z.number().int().nonnegative(),
    travelTicks: z.number().int().positive(),
    targetMask: z.literal('all-enemies'),
    towerDamageMultiplier: z.number().min(0).max(1),
    visualKey: z.string().min(1),
    behavior: z.discriminatedUnion('type', [
      z.object({ type: z.literal('line-damage'), distance: z.number().int().positive(), speedPerTick: z.number().int().positive(), knockback: z.number().int().nonnegative(), targetMovement: z.literal('ground') }),
      z.object({ type: z.literal('spawn-units'), groups: z.array(spawn).min(1) }),
    ]).optional(),
  })
  .superRefine((spell, context) => {
    if (spell.behavior?.type === 'spawn-units') {
      if (spell.damage !== 0 || spell.radius !== 0 || spell.towerDamageMultiplier !== 0) {
        context.addIssue({ code: 'custom', message: 'spawn-units spell must not declare placeholder damage' });
      }
      return;
    }

    if (spell.damage === 0 || spell.radius === 0 || spell.towerDamageMultiplier === 0) {
      context.addIssue({ code: 'custom', message: 'damage spell requires positive damage, radius and towerDamageMultiplier' });
    }
  });

export const arenaDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  nameKey: z.string().min(1),
  schemaVersion: z.literal(1),
  gameplay: z.object({
    geometryId: z.string().min(1),
    worldBounds: z.object({ x: z.number().int(), y: z.number().int(), width: z.number().int().positive(), height: z.number().int().positive() }),
    riverTop: z.number().int(),
    riverBottom: z.number().int(),
    bridges: z.array(z.object({ id: z.string().min(1), x: z.number().int(), width: z.number().int().positive() })).min(1),
    playerDeployMinY: z.number().int(),
    botDeployMaxY: z.number().int(),
    towerSockets: z.object({
      playerLeft: point,
      playerRight: point,
      playerKing: point,
      botLeft: point,
      botRight: point,
      botKing: point,
    }),
    deploymentZones: z.array(z.object({
      id: z.string().min(1),
      owner: z.enum(['player', 'bot']),
      laneId: z.string().min(1).nullable(),
      bounds: z.object({ x: z.number().int(), y: z.number().int(), width: z.number().int().positive(), height: z.number().int().positive() }),
      unlockTowerSocketId: z.enum(['playerLeft', 'playerRight', 'playerKing', 'botLeft', 'botRight', 'botKing']).optional(),
    })).min(2),
  }),
  presentation: z.object({
    assetManifestKey: z.string().min(1),
    backgroundKey: z.string().min(1),
    towerPlatform: z.object({ textureKey: z.string().min(1), princessSize: z.number().int().positive(), kingSize: z.number().int().positive() }),
    camera: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
    palette: z.object({ player: z.string(), bot: z.string(), river: z.string() }),
  }),
  rulesetCompatibility: z.array(z.string().min(1)).min(1),
});

const projectilePresentation = z.object({
  shape: z.enum(['fire', 'bullet', 'bomb', 'spit', 'cannonball', 'arrow', 'orb']).optional(),
  texture: z.string().min(1).optional(),
  width: z.number().positive().optional(),
  radius: z.number().positive(),
  fillColor: color,
  strokeColor: color,
  trailColor: color,
  impactColor: color,
  impactStrokeColor: color,
  impactRadius: z.number().positive(),
  flightOffsetY: z.number().optional(),
  launchOffset: z.object({
    forward: z.number(),
    side: z.number(),
    settleSeconds: z.number().positive().max(0.5),
  }).optional(),
  animation: z.object({
    pulseHz: z.number().positive(),
    pulseAmount: z.number().min(0).max(0.5),
  }).optional(),
  trail: z.object({
    color,
    coreColor: color,
    emberColor: color,
    radius: z.number().positive(),
    lifetimeSeconds: z.number().positive().max(2),
    emitIntervalSeconds: z.number().positive().max(1),
    drift: z.number().nonnegative(),
  }).optional(),
  impact: z.object({
    durationSeconds: z.number().positive().max(2),
    radius: z.number().positive(),
    coreColor: color,
    fireColor: color,
    hotColor: color,
    shockwaveColor: color,
    smokeColor: color,
    flameCount: z.number().int().min(1).max(16),
    sparkCount: z.number().int().min(0).max(24),
  }).optional(),
  targetHit: z.object({
    durationSeconds: z.number().positive().max(1),
    color,
    hotColor: color,
  }).optional(),
});

export const presentationDefinitionSchema = z.object({
  id: z.string().min(1),
  texture: z.string().min(1),
  visualScale: z.number().positive(),
  anchor: z.tuple([z.number(), z.number()]),
  healthBarOffsetY: z.number(),
  shadow: z.object({
    width: z.number().positive(),
    height: z.number().positive(),
    x: z.number().optional(),
    y: z.number().optional(),
    skew: z.number().optional(),
  }).optional(),
  flightOffsetY: z.number().optional(),
  projectile: projectilePresentation.optional(),
  attackCue: z.object({
    durationSeconds: z.number().positive().max(1),
    radius: z.number().positive(),
    offsetY: z.number(),
    forward: z.number().optional(),
    side: z.number().optional(),
    color,
    hotColor: color,
  }).optional(),
  sprite: z.object({
    asset: z.string().min(1),
    player: z.string().min(1),
    bot: z.string().min(1),
    family: z.enum(['units', 'buildings']).optional(),
    mirrorX: z.boolean().optional(),
  }).optional(),
  requiredStates: z.array(z.string().min(1)).min(1),
});
